import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, X, BadgeCheck, FileCheck2, Download, LayoutGrid, ShieldCheck, ExternalLink } from "lucide-react";
import { STANDARDS, STANDARD_BY_KEY, FEATURE_GROUPS } from "@/constants/compliance";
import HubCmdLogo from "@/components/HubCmdLogo";
import FeedbackButton from "@/components/FeedbackButton";
import AuditPackDialog from "@/components/AuditPackDialog";

export default function Compliance() {
  const [filter, setFilter] = useState(null); // standard key
  const [query, setQuery] = useState("");
  const [view, setView] = useState("feature"); // "feature" | "standard"
  const [packOpen, setPackOpen] = useState(false);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FEATURE_GROUPS.map((g) => ({
      ...g,
      features: g.features.filter((f) => {
        if (filter && !f.citations.some((c) => c.std === filter)) return false;
        if (!q) return true;
        const hay = (f.title + " " + f.summary + " " + g.group).toLowerCase();
        return hay.includes(q);
      }),
    })).filter((g) => g.features.length > 0);
  }, [filter, query]);

  // Inverse pivot: for each standard, which features cite it?
  const byStandard = useMemo(() => {
    const q = query.trim().toLowerCase();
    return STANDARDS
      .filter((s) => !filter || s.key === filter)
      .map((s) => {
        const matches = [];
        FEATURE_GROUPS.forEach((g) => {
          g.features.forEach((f) => {
            const cite = f.citations.find((c) => c.std === s.key);
            if (!cite) return;
            if (q) {
              const hay = (f.title + " " + f.summary + " " + g.group + " " + s.name).toLowerCase();
              if (!hay.includes(q)) return;
            }
            matches.push({ group: g.group, feature: f, clauses: cite.clauses });
          });
        });
        return { standard: s, matches };
      })
      .filter((row) => row.matches.length > 0);
  }, [filter, query]);

  const totalFeatures = FEATURE_GROUPS.reduce((n, g) => n + g.features.length, 0);
  const totalCitations = FEATURE_GROUPS.reduce((n, g) =>
    n + g.features.reduce((m, f) => m + f.citations.reduce((k, c) => k + c.clauses.length, 0), 0), 0);

  return (
    <div className="min-h-screen bg-[#f7f5f2] text-[#1a1a1a]">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur bg-[#1a1a1a]/95 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 group" data-testid="compliance-home">
            <HubCmdLogo size={32} variant="shield" inverted />
            <span className="font-display font-black text-lg tracking-tight"><span>hub</span><span className="text-[#ff5f15]">CMD</span></span>
            <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">// Compliance map</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-[12px] font-bold uppercase tracking-wider text-white/85 hover:text-white">Sign in</Link>
            <Link to="/register" className="btn-pill btn-pill-primary">Get started <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-[#1a1a1a] text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
          <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-white/65 hover:text-white" data-testid="compliance-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <div className="mt-3 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 backdrop-blur-sm">
            <BadgeCheck className="w-3.5 h-3.5 text-[#ff5f15]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em]">{STANDARDS.length} standards · {totalFeatures} features · {totalCitations}+ clause citations</span>
          </div>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            Built for the audit.<br/>
            <span className="text-[#ff5f15]">Every clause they ask about.</span>
          </h1>
          <p className="mt-4 text-base text-white/80 max-w-2xl">
            hubCMD is engineered against the standards your auditors actually open the binder for. Use the table below to look up exactly which feature satisfies which clause across <b className="text-white">23 frameworks</b> spanning quality, food safety, medical, aerospace, environmental, energy, info-security, and asset management.
          </p>
          <div className="mt-2 text-[11px] font-mono uppercase tracking-wider text-white/55">
            Clause references are best-effort and provided as a starting point — always validate against the controlling document for your audit.
          </div>
        </div>
      </section>

      {/* Standards strip */}
      <section className="bg-white border-y border-[#1a1a1a]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-3">// Filter by standard</div>
          <div className="flex flex-wrap gap-1.5" data-testid="standards-filter">
            <button data-testid="filter-all" onClick={() => setFilter(null)}
              className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-3 py-1.5 transition-colors ${filter == null ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20 hover:border-[#1a1a1a]"}`}>
              All standards
            </button>
            {STANDARDS.map((s) => {
              const active = filter === s.key;
              return (
                <button key={s.key} data-testid={`filter-${s.key}`} onClick={() => setFilter(active ? null : s.key)}
                  title={s.short}
                  className={`text-[10px] font-bold uppercase tracking-wider rounded-full border px-3 py-1.5 transition-colors ${active ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15 hover:border-[#ff4500] hover:text-[#ff4500]"}`}>
                  {s.name}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#1a1a1a]/40" />
              <input data-testid="compliance-search" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search features (e.g. 'signature', 'risk', 'maintenance')…"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-full pl-9 pr-9 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
              {query && (
                <button data-testid="compliance-search-clear" onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1a1a1a]/40 hover:text-[#1a1a1a]"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <div className="inline-flex rounded-full bg-white border border-[#1a1a1a]/15 p-0.5" role="group" data-testid="view-toggle">
              <button data-testid="view-by-feature" onClick={() => setView("feature")}
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5 ${view === "feature" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/65 hover:text-[#1a1a1a]"}`}>
                <LayoutGrid className="w-3.5 h-3.5" /> By feature
              </button>
              <button data-testid="view-by-standard" onClick={() => setView("standard")}
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider rounded-full px-3 py-1.5 ${view === "standard" ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/65 hover:text-[#1a1a1a]"}`}>
                <ShieldCheck className="w-3.5 h-3.5" /> By standard
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Feature × clause table */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {view === "feature" && groups.length === 0 && (
          <div className="bg-white border border-[#1a1a1a]/10 rounded-2xl p-10 text-center" data-testid="compliance-empty">
            <FileCheck2 className="w-10 h-10 text-[#1a1a1a]/30 mx-auto" />
            <div className="font-display font-bold text-lg mt-3">No matches</div>
            <div className="text-[12px] text-[#1a1a1a]/65 mt-1">Try a different standard or clear your search.</div>
          </div>
        )}
        {view === "feature" && groups.map((g) => (
          <div key={g.group} className="mb-10" data-testid={`compliance-group-${g.group.replace(/\s+/g,'-').toLowerCase()}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff4500]">// Module</div>
              <h2 className="font-display font-black text-2xl tracking-tight">{g.group}</h2>
            </div>
            <div className="space-y-4">
              {g.features.map((f) => (
                <article key={f.id} className="bg-white border border-[#1a1a1a]/10 rounded-2xl overflow-hidden" data-testid={`feature-${f.id}`}>
                  <div className="bg-gradient-to-br from-[#fff1ea] to-white border-b border-[#1a1a1a]/05 px-5 py-4">
                    <h3 className="font-display font-black text-lg tracking-tight">{f.title}</h3>
                    <p className="text-[13px] text-[#1a1a1a]/75 mt-1 leading-relaxed">{f.summary}</p>
                  </div>
                  <div className="px-5 py-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/55 mb-2">// Standards & clauses satisfied</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {f.citations.filter((c) => !filter || c.std === filter).map((c) => {
                        const std = STANDARD_BY_KEY[c.std];
                        return (
                          <div key={c.std} className="bg-[#f7f5f2] rounded-lg border border-[#1a1a1a]/05 p-2.5">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-[#1a1a1a] text-white rounded-full px-2 py-0.5">{std?.name}</span>
                              <span className="text-[10px] font-mono text-[#1a1a1a]/55 truncate">{std?.short}</span>
                            </div>
                            <ul className="space-y-0.5">
                              {c.clauses.map((cl, i) => (<li key={i} className="text-[11px] text-[#1a1a1a]/85 font-mono">· {cl}</li>))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ))}

        {/* By-standard pivot */}
        {view === "standard" && byStandard.length === 0 && (
          <div className="bg-white border border-[#1a1a1a]/10 rounded-2xl p-10 text-center" data-testid="compliance-empty-std">
            <FileCheck2 className="w-10 h-10 text-[#1a1a1a]/30 mx-auto" />
            <div className="font-display font-bold text-lg mt-3">No matches</div>
            <div className="text-[12px] text-[#1a1a1a]/65 mt-1">Try clearing the standard filter or search.</div>
          </div>
        )}
        {view === "standard" && byStandard.map(({ standard, matches }) => (
          <div key={standard.key} className="mb-8" data-testid={`std-block-${standard.key}`}>
            <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff4500]">// Standard</div>
                <h2 className="font-display font-black text-2xl tracking-tight">{standard.name}</h2>
                <div className="text-[12px] text-[#1a1a1a]/65 mt-0.5">{standard.short}</div>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-wider bg-[#1a1a1a] text-white rounded-full px-2 py-0.5">{matches.length} hubCMD feature{matches.length === 1 ? "" : "s"}</span>
            </div>
            <div className="bg-white border border-[#1a1a1a]/10 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#f7f5f2]">
                  <tr className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1a1a1a]/55 text-left">
                    <th className="py-2 px-3 w-44">Module</th>
                    <th className="py-2 px-3">Feature</th>
                    <th className="py-2 px-3 w-2/5">Clauses satisfied</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m, i) => (
                    <tr key={`${standard.key}-${m.feature.id}`} className={`${i === 0 ? "" : "border-t border-[#1a1a1a]/05"}`} data-testid={`std-row-${standard.key}-${m.feature.id}`}>
                      <td className="py-3 px-3 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 align-top whitespace-nowrap">{m.group}</td>
                      <td className="py-3 px-3 align-top">
                        <div className="font-display font-bold text-sm">{m.feature.title}</div>
                        <div className="text-[12px] text-[#1a1a1a]/65 mt-0.5 leading-snug">{m.feature.summary}</div>
                      </td>
                      <td className="py-3 px-3 align-top">
                        <ul className="space-y-0.5">
                          {m.clauses.map((cl, ix) => (<li key={ix} className="text-[11px] text-[#1a1a1a]/85 font-mono">· {cl}</li>))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>

      {/* CTA · audit-readiness pack + TruPath cross-promo */}
      <section className="bg-[#1a1a1a] text-white relative overflow-hidden">
        <div className="absolute inset-0 dotted opacity-15" />
        <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#ff4500]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white/5 border border-white/15 rounded-3xl p-6 lg:p-7 backdrop-blur-sm" data-testid="audit-pack-block">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff5f15]">// Free lead magnet</div>
            <h2 className="mt-1 font-display font-black text-2xl lg:text-3xl tracking-tight leading-tight">Get the audit-readiness pack.</h2>
            <p className="mt-2 text-[13px] text-white/75 leading-relaxed max-w-md">
              Pick your target standard. We'll auto-generate a customized PDF showing every clause auditors will hit and exactly how hubCMD addresses it. Signed, dated, ready to drop into your binder.
            </p>
            <button data-testid="audit-pack-open" onClick={() => setPackOpen(true)}
              className="mt-4 btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] shadow-glow font-display font-black text-base px-5 py-2.5">
              <Download className="w-4 h-4" /> Request the pack
            </button>
          </div>
          <a href="https://trupathadvisory.com" target="_blank" rel="noopener noreferrer"
            data-testid="trupath-cross-promo"
            className="bg-gradient-to-br from-[#ff4500]/30 via-[#ff5f15]/15 to-transparent border border-[#ff5f15]/30 rounded-3xl p-6 lg:p-7 hover:border-[#ff5f15] transition-colors block group">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff5f15]">// Companion service</div>
            <h2 className="mt-1 font-display font-black text-2xl lg:text-3xl tracking-tight leading-tight">
              TruPath Advisory<br/><span className="text-[#ff5f15]">expert-led implementation.</span>
            </h2>
            <p className="mt-2 text-[13px] text-white/75 leading-relaxed max-w-md">
              hubCMD is the software; <b className="text-white">TruPath Advisory</b> is the consulting firm behind it — Lean Manufacturing, Continuous Improvement, and third-party audit program design &amp; implementation. Get hands-on guidance from the people who built the platform.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-wider text-white group-hover:text-[#ff5f15]">
              Visit TruPathAdvisory.com <ExternalLink className="w-3.5 h-3.5" />
            </div>
          </a>
        </div>
      </section>

      {/* Trial CTA strip */}
      <section className="bg-[#0e0e0e] text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#ff5f15]">// Ready when the auditor walks in?</div>
            <h2 className="font-display font-black text-2xl mt-1">Stand up your operations hub today.</h2>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/register" data-testid="compliance-cta-register" className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] shadow-glow font-display font-black text-base px-5 py-2.5">
              Start free trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/" className="btn-pill btn-pill-ghost border border-white/20">Back to overview</Link>
          </div>
        </div>
      </section>

      <footer className="bg-[#0e0e0e] text-white/60 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 text-[11px] font-mono uppercase tracking-wider flex flex-col md:flex-row justify-between gap-2">
          <span>© hubCMD · audit-ready operations · {new Date().getFullYear()}</span>
          <a href="https://trupathadvisory.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff5f15]" data-testid="footer-trupath">A TruPath Advisory product →</a>
        </div>
      </footer>

      <AuditPackDialog open={packOpen} onClose={() => setPackOpen(false)} />
      <FeedbackButton variant="floating" />
    </div>
  );
}
